import { Alert, Button, Group, List, Modal, Paper, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ImportConnectivityForm from '../import-connectivity-form/import-connectivity-form';

import { usePermissions } from '@auth/data-access';
import { FORM_STATUS, sharedApi, useSharedQueries } from '@data-access';

const root = import.meta.env.VITE_API_ROOT;

interface ConnectivityCardProps {
  description: string;
  tutorial?: string;
  is_import: boolean;
  id: number;
  is_active: boolean;
}

export function ConnectivityCard({
  description,
  tutorial,
  id,
  is_active,
}: ConnectivityCardProps) {
  const { t } = useTranslation();
  const { isAdmin } = usePermissions();
  const [opened, actions] = useDisclosure();
  const [exportOpened, exportActions] = useDisclosure();
  const [errorsModalOpened, errorsModalActions] = useDisclosure();
  const [shownErrorId, setShownErrorId] = useState<number | null>(null);

  const sharedQueries = useSharedQueries();

  const importFile = useMutation({
    mutationFn: sharedApi.importConnectivity,
  });

  const submitImportForReview = useMutation({
    mutationFn: sharedApi.submitImportForReview,
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: t('connectivity.submit-success'),
        position: 'bottom-right',
      });
    },
    onError: () => {
      notifications.show({
        color: 'red',
        message: t('connectivity.submit-error'),
        position: 'bottom-right',
      });
    },
  });

  const activeImportMutation = isAdmin ? importFile : submitImportForReview;

  const exportFile = useMutation({
    mutationFn: sharedApi.exportConnectivity,
    onSuccess: () => {
      exportActions.close();
    },
  });

  const importStatus = useQuery({
    ...sharedQueries.computationRequest({
      id: importFile.data?.async_computation_request_id as number,
    }),
    enabled: isAdmin && !!importFile.data?.async_computation_request_id,
  });

  const isImportPending = activeImportMutation.isPending;
  const isImportFinished = isAdmin
    ? importStatus.data?.status === FORM_STATUS.FINISHED
    : submitImportForReview.isSuccess;
  const isImportError =
    isAdmin
      ? importFile.isError ||
        importStatus.isError ||
        importStatus.data?.status === FORM_STATUS.ERROR
      : submitImportForReview.isError;

  const isImporting =
    isImportPending || (isAdmin && importStatus.isSuccess && !isImportFinished);

  const importErrored = isImportError;
  const importSucceed = isImportFinished;

  // Parse errors and warnings from JSON strings
  const parsedErrors = useMemo(() => {
    if (!importStatus.data?.errors) return [];
    try {
      return JSON.parse(importStatus.data.errors) as string[];
    } catch {
      return [importStatus.data.errors];
    }
  }, [importStatus.data?.errors]);

  const parsedWarnings = useMemo(() => {
    if (!importStatus.data?.warnings) return [];
    try {
      return JSON.parse(importStatus.data.warnings) as string[];
    } catch {
      return [importStatus.data.warnings];
    }
  }, [importStatus.data?.warnings]);


  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    if (importStatus.data?.status === FORM_STATUS.FINISHED) {
      notifications.show({
        color: 'green',
        message: 'Import réussi',
        position: 'bottom-right',
      });
    } else if (
      importStatus.data?.status === FORM_STATUS.ERROR &&
      importStatus.data?.id !== shownErrorId
    ) {
      // Open errors modal when import fails (only once per error)
      setShownErrorId(importStatus.data.id);
      errorsModalActions.open();
    }
  }, [importStatus.data?.status, importStatus.data?.id, errorsModalActions, shownErrorId, isAdmin]);

  return (
    <Paper h={190} p="sm" w={220}>
      <Stack gap="sm" h="100%" justify="space-between">
        <Title lineClamp={2} mih="3.2em" order={5} ta="center">
          {description}
        </Title>

        <Stack gap="xs">
          <Button
            color={importErrored ? 'red' : 'pf-green'}
            disabled={!is_active}
            fullWidth
            loading={isImporting && !importErrored}
            onClick={() => {
              activeImportMutation.reset();
              if (isAdmin) {
                importFile.reset();
                setShownErrorId(null);
              }
              actions.open();
            }}
            rightSection={
              importErrored ? <IconX /> : importSucceed ? <IconCheck /> : null
            }
            size="compact-sm"
          >
            {isAdmin ? t('connectivity.import') : t('connectivity.submit-for-review')}
          </Button>
          <Button
            disabled={!is_active}
            fullWidth
            onClick={exportActions.open}
            size="compact-sm"
            variant="light"
          >
            {t('common.export')}
          </Button>
          <Button
            component={tutorial ? 'a' : 'button'}
            disabled={!is_active || !tutorial}
            fullWidth
            href={tutorial ? root + tutorial : undefined}
            rel="noreferrer"
            size="compact-sm"
            style={{ visibility: tutorial ? 'visible' : 'hidden' }}
            target="_blank"
            variant="subtle"
          >
            {t('connectivity.tutorial')}
          </Button>
        </Stack>
      </Stack>
      <ImportConnectivityForm
        loading={activeImportMutation.isPending}
        onClose={actions.close}
        onSubmit={(values) => {
          if (isAdmin) {
            importFile.reset();
            setShownErrorId(null);
            importFile.mutate({
              id,
              data_file: values.data_file,
              fields_file: values.fields_file,
              format: values.format,
            });
            return;
          }

          submitImportForReview.mutate({
            id,
            data_file: values.data_file,
            fields_file: values.fields_file,
            format: values.format,
          });
        }}
        opened={opened}
        submitLabel={
          isAdmin
            ? t('connectivity.import')
            : t('connectivity.submit-for-review')
        }
      />
      <Modal
        onClose={exportActions.close}
        opened={exportOpened}
        title={t('common.export')}
      >
        <Stack>
          <Group justify="flex-end" mt="md">
            <Button
              color="red"
              onClick={exportActions.close}
              variant="light"
            >
              {t('common.cancel')}
            </Button>
            <Button
              disabled={!is_active}
              loading={exportFile.isPending}
              onClick={() => {
                exportFile.mutate({
                  id,
                });
              }}
            >
              {t('common.export')}
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Modal
        onClose={errorsModalActions.close}
        opened={errorsModalOpened}
        size="lg"
        title={t('common.ERROR')}
      >
        <Stack gap="md">
          <Text c="red" size="sm">
            Des erreurs ont été trouvées, aucune ligne n'a été importée. Veuillez les corriger et importer à nouveau le fichier.
          </Text>
          <ScrollArea h={400}>
            <Stack gap="md">
              {parsedErrors.length > 0 && (
                <Alert color="red" icon={<IconAlertCircle />} title={t('common.ERROR')}>
                  <List>
                    {parsedErrors.map((error, index) => (
                      <List.Item key={index}>{error}</List.Item>
                    ))}
                  </List>
                </Alert>
              )}
              {parsedWarnings.length > 0 && (
                <Alert color="yellow" icon={<IconAlertCircle />} title="Avertissements">
                  <List>
                    {parsedWarnings.map((warning, index) => (
                      <List.Item key={index}>{warning}</List.Item>
                    ))}
                  </List>
                </Alert>
              )}
            </Stack>
          </ScrollArea>
          <Group justify="flex-end" mt="md">
            <Button onClick={errorsModalActions.close}>
              {t('common.close')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}

export default ConnectivityCard;
